import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React from "react";

import { patientAdditionalNotesApi } from "../../../apis";
import { AdditionalNotesResponseDto } from "../../../libs/cellera-sdk";
import { useCelleraTheme } from "../../../theme/useCelleraTheme";
import IntakeField from "./IntakeField";
import IntakeHeader from "./IntakeHeader";
import LayoutWrapper from "./LayoutWrapper";

interface AdditionalNotesProps {
  patientId: string;
}

const AdditionalNotes: React.FC<AdditionalNotesProps> = ({ patientId }) => {
  const { colors } = useCelleraTheme();

  const { data, isError, error } = useQuery({
    queryKey: ["additionalNotes", patientId],
    queryFn: async () => {
      if (!patientId) return null;
      return patientAdditionalNotesApi.getAdditionalNotesByPatientId({
        patientId,
      });
    },
    enabled: !!patientId,
  });

  React.useEffect(() => {
    if (isError && error) {
      console.error("AdditionalNotes query error:", error);
    }
  }, [isError, error]);

  const displayData: AdditionalNotesResponseDto = data ?? {
    id: "",
    patientId,
    additionalImportantInformation: "",
    comfortableWithInjectables: false,
    expectedMonthlyBudget: "",
    therapiesTriedPreviously: "",
  };

  const isEmpty =
    !displayData.additionalImportantInformation &&
    !displayData.expectedMonthlyBudget &&
    !displayData.therapiesTriedPreviously;

  return (
    <>
      <IntakeHeader title="Additional Notes" />

      {isEmpty ? (
        <Box
          sx={{
            backgroundColor: colors.background.paper,
            borderRadius: "8px",
            border: `1px solid ${colors.border.light}`,
          }}
        >
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color={colors.text.secondary} gutterBottom>
              No Additional Notes Found
            </Typography>
            <Typography variant="body2" color={colors.text.secondary}>
              This patient has not filled out their additional notes yet.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box>
          {/* Fields in 1-column layout for better readability */}
          <LayoutWrapper itemsPerRow={1} spacing={3}>
            <IntakeField
              label="Comfortable with injectables?"
              value={displayData.comfortableWithInjectables ? "Yes" : "No"}
              disabled={true}
            />

            <IntakeField
              label="Expected monthly budget"
              value={
                displayData.expectedMonthlyBudget
                  ? `$${displayData.expectedMonthlyBudget}`
                  : ""
              }
              disabled={true}
            />

            <IntakeField
              label="Therapies tried previously"
              value={displayData.therapiesTriedPreviously || ""}
              disabled={true}
              multiline={true}
            />

            <IntakeField
              label="Additional Information (Optional)"
              value={displayData.additionalImportantInformation || ""}
              disabled={true}
              multiline={true}
            />
          </LayoutWrapper>
        </Box>
      )}
    </>
  );
};

export default AdditionalNotes;
